// src/components/layout/Sidebar.js
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    HomeIcon,
    CubeIcon,
    TagIcon,
    ShoppingCartIcon,
    ClipboardDocumentListIcon,
    ClipboardDocumentCheckIcon,
    TruckIcon,
    UsersIcon,
    Cog6ToothIcon,
    DocumentTextIcon,
    ArrowRightOnRectangleIcon,
    XMarkIcon,
    PuzzlePieceIcon,
    ArchiveBoxIcon,
    BuildingStorefrontIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    UserGroupIcon,
    ChartBarIcon as ChartBarIconOutline,
    EyeIcon,
    PencilIcon
} from '@heroicons/react/24/outline';
import {TbDatabaseImport} from "react-icons/tb";
import logoSrc from "../../assets/img/fit2ride-logo.png";
import {ChartBarIcon} from "@heroicons/react/24/solid";

const Sidebar = ({ isOpen, closeSidebar }) => {
    const { currentUser, hasRole, hasAnyRole, logout } = useAuth();
    const [expandedMenus, setExpandedMenus] = useState({});

    const toggleSubmenu = (menuName) => {
        setExpandedMenus(prev => ({
            ...prev,
            [menuName]: !prev[menuName]
        }));
    };

    const navItems = [
        {
            name: 'Dashboard',
            icon: HomeIcon,
            to: '/dashboard',
            allowed: true, // All authenticated users can access
        },
        {
            name: 'Production/Picking',
            icon: ClipboardDocumentListIcon,
            to: '/production',
            allowed: hasAnyRole(['admin', 'production']),
        },
        {
            name: 'Packing Slips',
            icon: ClipboardDocumentCheckIcon,
            to: '/packing-slips',
            allowed: hasAnyRole(['admin', 'production']),
        },
        {
            name: 'Parts Inventory',
            icon: CubeIcon,
            to: '/inventory',
            allowed: true, // All authenticated users can access
        },
        {
            name: 'Product Inventory',
            icon: ArchiveBoxIcon,
            to: '/stock',
            allowed: hasAnyRole(['admin', 'procurement', 'production']),
        },
        {
            name: 'Products',
            icon: TagIcon,
            to: '/products',
            allowed: hasAnyRole(['admin', 'procurement', 'production']),
        },
        {
            name: 'Orders',
            icon: ShoppingCartIcon,
            to: '/orders',
            allowed: hasAnyRole(['admin', 'production']),
        },
        {
            name: 'Customers',
            icon: UserGroupIcon,
            to: '/customers',
            allowed: hasAnyRole(['admin', 'procurement']),
        },
        {
            name: 'Part Orders',
            icon: TruckIcon,
            to: '/part-orders',
            allowed: hasAnyRole(['admin', 'procurement']),
        },
        {
            name: 'Supply Orders',
            icon: ArchiveBoxIcon,
            to: '/supply-orders',
            allowed: hasAnyRole(['admin', 'procurement']),
        },
        {
            name: 'Supply Forecast',
            icon: ChartBarIcon,
            to: '/supply-forecast',
            allowed: hasAnyRole(['admin', 'procurement']),
        },
        {
            name: 'Part Types',
            icon: PuzzlePieceIcon,
            to: '/part-types',
            allowed: hasAnyRole(['admin', 'procurement']),
        },
        {
            name: 'Vendors',
            icon: BuildingStorefrontIcon,
            to: '/vendors',
            allowed: hasAnyRole(['admin', 'procurement']),
        },
        {
            name: 'Reports',
            icon: DocumentTextIcon,
            to: '/reports',
            allowed: hasAnyRole(['admin', 'procurement']),
        },
        {
            name: 'Users',
            icon: UsersIcon,
            to: '/users',
            allowed: hasRole('admin'),
        },
        {
            name: 'Settings',
            icon: Cog6ToothIcon,
            to: '/settings',
            allowed: true, // All authenticated users can access their settings
        },
    ];

    const sidebarClass = `bg-secondary-800 text-white w-64 flex flex-col h-screen fixed z-30
                         transition-transform duration-300 ease-in-out sidebar
                         md:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`;

    const renderNavItem = (item) => {
        if (!item.allowed) return null;

        if (item.hasSubmenu) {
            const isExpanded = expandedMenus[item.name];

            return (
                <li key={item.name}>
                    <div>
                        <button
                            onClick={() => toggleSubmenu(item.name)}
                            className="w-full flex items-center justify-between p-2 rounded-md text-secondary-300 hover:bg-secondary-700 focus:outline-none"
                        >
                            <div className="flex items-center">
                                <item.icon className="h-5 w-5 mr-2" />
                                {item.name}
                            </div>
                            {isExpanded ? (
                                <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                                 <ChevronRightIcon className="h-4 w-4" />
                             )}
                        </button>

                        {/* Submenu */}
                        <div className={`ml-4 mt-1 space-y-1 transition-all duration-200 ${
                            isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                        }`}>
                            {item.submenu?.map((subItem) =>
                                                   subItem.allowed ? (
                                                       <NavLink
                                                           key={subItem.name}
                                                           to={subItem.to}
                                                           className={({ isActive }) =>
                                                               `flex items-center p-2 pl-4 rounded-md text-sm ${
                                                                   isActive
                                                                   ? 'bg-primary-700 text-white'
                                                                   : 'text-secondary-300 hover:bg-secondary-700'
                                                               }`
                                                           }
                                                           onClick={() => {
                                                               if (window.innerWidth < 768) {
                                                                   closeSidebar();
                                                               }
                                                           }}
                                                       >
                                                           <subItem.icon className="h-4 w-4 mr-2" />
                                                           {subItem.name}
                                                       </NavLink>
                                                   ) : null
                            )}
                        </div>
                    </div>
                </li>
            );
        }

        return (
            <li key={item.name}>
                <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                        `flex items-center p-2 rounded-md ${
                            isActive
                            ? 'bg-primary-700 text-white'
                            : 'text-secondary-300 hover:bg-secondary-700'
                        }`
                    }
                    onClick={() => {
                        if (window.innerWidth < 768) {
                            closeSidebar();
                        }
                    }}
                >
                    <item.icon className="h-5 w-5 mr-2" />
                    {item.name}
                </NavLink>
            </li>
        );
    };

    return (
        <div className={sidebarClass}>
            <div className="p-4 border-b border-secondary-700 flex justify-between items-center">
                <div className="flex flex-row items-center justify-between gap-1">
                    <img src={logoSrc} alt="logo" className="h-[50px] w-[50px]" />
                    <div className={``}>
                        <h1 className="text-2xl font-bold">Fit Protection</h1>
                        <p className="text-sm text-secondary-300">Inventory Management</p>
                    </div>
                </div>

                {/* Close button - visible only on mobile */}
                <button
                    className="md:hidden text-secondary-300 hover:text-white"
                    onClick={closeSidebar}
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4">
                <ul className="space-y-2">
                    {navItems.map(renderNavItem)}
                </ul>
            </nav>

            <div className="p-4 border-t border-secondary-700">
                <button
                    onClick={logout}
                    className="w-full flex items-center p-2 rounded-md text-secondary-300 hover:bg-secondary-700"
                >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                    Logout
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
